import React, { useState, useEffect } from 'react';

import { Alert } from 'antd';
import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { capitalize } from '@libs/NwUtils';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { forceReloadEvent } from '@hooks/API/Event/UseEvent';
import { forceReloadProject } from '@hooks/API/Project/UseProject';
import { forceReloadProjectAccountingDetails } from '@hooks/API/Project/UseProjectAccountingDetails';
import { forceReloadProjectTimeline } from '@hooks/API/Project/UseProjectTimeline';
import useFormFieldFocus from '@hooks/Utils/UseFormFieldFocus';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { NWInput, NWContactDropdown, NWSwitch } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

const ProjectChangeCustomer = ({ project, onClose, eventID }) => {
    useFormFieldFocus('Name');
    const [touchForm, setTouchForm] = useState(false);
    const [errorSaving, setErrorSaving] = useState(null);

    const savedCustomerName = project.Contact ? project.Contact.Name : getContactName(project.CustomerID);
    const savedCustomerID = project.Contact ? project.Contact.ID : project.CustomerID;
    const [transactionClients, setTransactionClients] = useState([]);
    const [lastCustomerName, setLastCustomerName] = useState(savedCustomerName);

    let initialValues = {
        Name: project.Name,
        CustomerID: savedCustomerID,
        UpdateTransactions: true,
    };

    useEffect(() => {
        Axios.get(`/projects/gettransactionclients/${project.ID}`).then((res) => {
            if (res && Array.isArray(res)) {
                setTransactionClients(res);
            }
        });
    }, []);

    const saveProject = async (values, setSubmitting) => {
        setSubmitting(true);
        try {
            await Axios.post('/projects/modifyclient', {
                ProjectID: project.ID,
                CustomerID: values.CustomerID,
                Title: values.Name,
                UpdateTransactions: values.UpdateTransactions,
            });
            setSubmitting(false);
            forceReloadProject(project.ID);
            forceReloadProjectAccountingDetails(project.ID);
            forceReloadProjectTimeline(project.ID);
            if (eventID) {
                forceReloadEvent(eventID);
            }
            onClose();
        } catch (error) {
            setErrorSaving('There was an error saving the project. Please try again.');
            setSubmitting(false);
        }
    };

    const updateProjectName = (customerID, values, setFieldValue) => {
        if (!customerID || customerID === savedCustomerID) {
            return;
        }
        const newCustomerName = getContactName(customerID);
        const simpleRegex = new RegExp(lastCustomerName, 'gi');
        const newName = values.Name.replace(simpleRegex, capitalize(newCustomerName));
        setFieldValue('Name', newName);
        setLastCustomerName(newCustomerName);
    };

    return (
        <NwDrawer touchForm={touchForm} onTouchForm={setTouchForm} title={'Change Project Client'} onClose={onClose}>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { setSubmitting }) => {
                    saveProject(values, setSubmitting);
                }}
                validationSchema={Yup.object().shape({
                    Name: Yup.string().required('Content is required'),
                    CustomerID: Yup.string().required('Content is required'),
                })}
            >
                {({ isSubmitting, values, handleSubmit, setFieldValue }) => (
                    <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit} layout="vertical">
                        <Field
                            required
                            component={NWContactDropdown}
                            activeTypes={['cli', 'age', 'srv']}
                            activableTypes={[]}
                            label="Project Client"
                            name="CustomerID"
                            value={values.CustomerID}
                            onAfterChange={(value) => {
                                updateProjectName(value, values, setFieldValue);
                            }}
                        />
                        <Field
                            component={NWInput}
                            label="Project Title"
                            name="Name"
                            type="text"
                            id="Name"
                            value={values.Name}
                            required
                            maxLength={255}
                        />
                        {transactionClients.length > 0 && (
                            <>
                                <Field
                                    component={NWSwitch}
                                    label="Update Transactions"
                                    name="UpdateTransactions"
                                    disabled={!transactionClients.includes(savedCustomerID)}
                                    value={values.UpdateTransactions}
                                />
                                {transactionClients.length === 1 && !transactionClients.includes(savedCustomerID) && (
                                    <p>
                                        Transactions are linked to{' '}
                                        <strong>{getContactName(transactionClients[0])}</strong> and won't be updated
                                        automatically
                                    </p>
                                )}
                                {transactionClients.length > 1 &&
                                    (transactionClients.includes(savedCustomerID) ? (
                                        <>
                                            <p>
                                                Only transactions linked to <strong>{savedCustomerName}</strong> will be
                                                updated. This project includes also transactions linked to:
                                            </p>
                                            <ul>
                                                {transactionClients
                                                    .filter((client) => client !== savedCustomerID)
                                                    .map((client) => (
                                                        <li key={client}>
                                                            <strong>{getContactName(client)}</strong>
                                                        </li>
                                                    ))}
                                            </ul>
                                            <p>These transactions will not be updated automatically.</p>
                                        </>
                                    ) : (
                                        <>
                                            <p>Transactions in this project are linked to:</p>
                                            <ul>
                                                {transactionClients
                                                    .filter((client) => client !== savedCustomerID)
                                                    .map((client) => (
                                                        <li key={client}>
                                                            <strong>{getContactName(client)}</strong>
                                                        </li>
                                                    ))}
                                            </ul>
                                            <p>These transactions will not be updated automatically.</p>
                                        </>
                                    ))}
                            </>
                        )}
                        {errorSaving && (
                            <Alert
                                style={{ marginTop: '1rem' }}
                                message="Error"
                                description={errorSaving}
                                type="error"
                                showIcon
                            />
                        )}
                        <br />
                        <NwFormButtonsBar
                            left={
                                <NwCancelButton
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        setTouchForm(false);
                                        onClose();
                                    }}
                                />
                            }
                            right={<NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />}
                        />
                    </NwForm>
                )}
            </Formik>
        </NwDrawer>
    );
};

export default ProjectChangeCustomer;
